import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Box, CircularProgress } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import api from '../../../utils/api';
import dayjs from 'dayjs';

const SmsPerformancePerServer = ({ startDate, endDate, selectedRange, format = 'day' }) => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchData();
    }, [startDate, endDate, selectedRange, format]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            let from, to;
            
            if (selectedRange === 'custom' && startDate && endDate) {
                from = startDate;
                to = endDate;
            } else if (selectedRange === '7_days') {
                from = dayjs().subtract(7, 'day');
                to = dayjs();
            } else if (selectedRange === '30_days') {
                from = dayjs().subtract(30, 'day');
                to = dayjs();
            } else if (selectedRange === 'year') {
                from = dayjs().subtract(1, 'year');
                to = dayjs();
            } else {
                from = dayjs().subtract(7, 'day');
                to = dayjs();
            }

            const response = await api.get('/admin/statistics/sms/performance-per-server', {
                params: {
                    from: from.format('YYYY-MM-DD'),
                    to: to.format('YYYY-MM-DD'),
                    format: format
                }
            });

            if (response.data.success) {
                setData(response.data.data.statistics);
            } else {
                setError('Failed to load data');
            }
        } catch (err) {
            console.error('Error fetching SMS performance per server:', err);
            setError(err.response?.data?.message || 'Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Card sx={{ height: 500 }}>
                <CardContent sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress />
                </CardContent>
            </Card>
        );
    }

    if (error || !data || data.length === 0) {
        return (
            <Card sx={{ height: 500 }}>
                <CardContent sx={{ height: '100%' }}>
                    <Typography variant="h6" gutterBottom>
                        Server SMS Sending Performance
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        {error || 'No data available'}
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    // Prepare chart data
    const dates = data[0]?.dates || [];
    const series = data.map((server, index) => {
        const colors = ['#1976d2', '#388e3c', '#f57c00', '#d32f2f', '#7b1fa2', '#0288d1'];
        return {
            data: server.data,
            label: server.server,
            color: colors[index % colors.length]
        };
    });

    // Format dates for display
    const formattedDates = dates.map(date => {
        try {
            const dateObj = dayjs(date);
            if (format === 'month') {
                return dateObj.format('MMM YYYY');
            } else {
                return dateObj.format('MMM D');
            }
        } catch (e) {
            return date;
        }
    });

    return (
        <Card sx={{ height: 500 }}>
            <CardContent sx={{ height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                    Server SMS Sending Performance
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Total sent SMS per server
                </Typography>
                <Box sx={{ width: '100%' }}>
                    <LineChart
                        xAxis={[{
                            scaleType: 'band',
                            data: formattedDates,
                            label: ''
                        }]}
                        series={series}
                        height={400}
                    />
                </Box>
            </CardContent>
        </Card>
    );
};

export default SmsPerformancePerServer;
